import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ContentMasterProductThumb } from '../components/content-master/ContentMasterProductThumb';
import ContentMasterOtherSourcesBadge from '../components/content-master/ContentMasterOtherSourcesBadge';
import BulkGenerateConfirmModal, {
  type BulkGenerateConfirmParams,
} from '../components/content-master/BulkGenerateConfirmModal';
import BulkTranslateConfirmModal, {
  type BulkTranslateConfirmParams,
} from '../components/content-master/BulkTranslateConfirmModal';
import ContentMasterFilterModal, {
  type ContentMasterFilterModalResult,
} from '../components/content-master/ContentMasterFilterModal';
import SuperBatchConfirmModal, {
  type SuperBatchListingState,
  defaultSuperBatchListingState,
  toSuperBatchListingConfig,
} from '../components/content-master/SuperBatchConfirmModal';
import ContentMasterProBadge from '../components/content-master/ContentMasterProBadge';
import MarketplaceCategoryModal from '../components/content-master/MarketplaceCategoryModal';
import HoverTooltip from '../components/ui/HoverTooltip';
import ContentMasterAddonRequiredModal from '../components/content-master/ContentMasterAddonRequiredModal';
import ContentMasterQuotaExceededModal from '../components/content-master/ContentMasterQuotaExceededModal';
import EmptyState from '../components/EmptyState';
import Pagination from '../components/Pagination';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';
import {
  useContentMasterEntitlements,
  useMarketplaceListingAddonActive,
} from '../hooks/useContentMasterEntitlements';
import { isMarketplaceSource } from '../utils/marketplaceListing';
import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import { ContentMasterWorkspacePlatformView } from '../api/generated/models/ContentMasterWorkspacePlatformView';
import { MarketplaceProductFieldsView } from '../api/generated/models/MarketplaceProductFieldsView';
import {
  CONTENT_MASTER_IN_PROGRESS_TAB_STATUS,
  ContentMasterListFilters,
  type ContentMasterProcessingStatusFilter,
  type ContentMasterSource,
  type ContentMasterSuperBatchParams,
  clearContentMasterBulkSelection,
  clearContentMasterSuperBatchEstimate,
  clearPendingContentMasterHighlight,
  estimateContentMasterSuperBatch,
  fetchContentMasterProducts,
  isSilentForbiddenRejection,
  setContentMasterBulkSelection,
  setContentMasterFilters,
  setSkipNextContentMasterListFetch,
  setContentMasterQuotaModalVisible,
  setContentMasterSubscriptionInsufficientModalVisible,
  submitContentMasterBulkGenerate,
  submitContentMasterBulkTranslate,
  submitContentMasterSuperBatch,
  toggleContentMasterBulkSelection,
  type ContentMasterEmptyFieldPolicy,
} from '../store/slices/contentMasterSlice';
import { submitMarketplaceListingGenerate } from '../store/slices/marketplaceListingSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { store } from '../store/store';
import {
  areContentMasterListFiltersEqual,
  buildContentMasterListPath,
  type ContentMasterListLocationState,
  parseContentMasterListSearchParams,
  serializeContentMasterListFilters,
} from '../utils/contentMasterListUrl';
import {
  isWawiLanguageCodeAllowed,
  pickDefaultWawiLanguageCode,
  wawiLanguageLabel,
  wawiLanguageToApiValue,
} from '../utils/contentMasterWawiLanguage';
import {
  buildContentMasterChannelGroups,
  buildContentMasterChannelOptions,
  formatContentMasterChannelMeta,
  pickDefaultContentMasterChannelId,
  resolveContentMasterSourceLabel,
  writeLastSelectedChannelId,
} from '../utils/contentMasterWorkspace';
import {
  contentMasterStatusLabelKey,
  getContentMasterStatusClassName,
} from '../utils/contentMasterProcessingStatus';

const SEARCH_DEBOUNCE_MS = 650;

/**
 * Per-channel persistence of the "Show all products" toggle (marketplace
 * sources only - `includeUnassigned` on GET /content-master/products). Kept in
 * localStorage (NOT the URL) so each Verkaufskanal remembers its own choice.
 */
const INCLUDE_UNASSIGNED_STORAGE_PREFIX = 'cm_include_unassigned_';

const readIncludeUnassigned = (source: string): boolean => {
  try {
    return localStorage.getItem(INCLUDE_UNASSIGNED_STORAGE_PREFIX + source) === '1';
  } catch {
    return false;
  }
};

const persistIncludeUnassigned = (source: string, value: boolean): void => {
  try {
    if (value) {
      localStorage.setItem(INCLUDE_UNASSIGNED_STORAGE_PREFIX + source, '1');
    } else {
      localStorage.removeItem(INCLUDE_UNASSIGNED_STORAGE_PREFIX + source);
    }
  } catch {
    // Storage unavailable (private mode / quota) - the toggle simply won't
    // survive a reload.
  }
};

const ContentMasterSearchIcon: React.FC = () => (
  <svg
    className="content-master-search__icon-svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M11 19a8 8 0 100-16 8 8 0 000 16z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 21l-4.35-4.35"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContentMasterFilterIcon: React.FC = () => (
  <svg
    className="content-master-filter-toggle__icon-svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M3 5h18l-7 8.5V19l-4 2v-7.5L3 5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContentMasterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const preferDeUi = i18n.language.toLowerCase().startsWith('de');
  // Subuser: bulk generation requires content_master_write; assigning a
  // marketplace category writes to Wawi and requires content_master_wawi_write.
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const canCmWawiWrite = can('content_master_wawi_write');
  const { bulkGenerationIncluded } = useContentMasterEntitlements();
  const marketplaceAddonActive = useMarketplaceListingAddonActive();
  // "Buy the marketplace-listing add-on" modal - opened when a Marktplatz
  // channel is selected in the search toolbar without an active add-on.
  const [isAddonRequiredModalOpen, setIsAddonRequiredModalOpen] = useState(false);

  /**
   * Sync URL → Redux SYNCHRONOUSLY during render, before `useAppSelector`
   * below reads `filters`.
   *
   * Doing this in a `useLayoutEffect` (as we used to) is too late: on a fresh
   * page reload with e.g. `?q=TEST-001&limit=50`, the layout effect's Redux
   * dispatch schedules a re-render but the `filters → URL` passive effect
   * from the FIRST commit still fires with its stale `filters` closure (the
   * slice's empty initial state, `q=''`, `limit=20`). That stale effect then
   * `setSearchParams`-replaces the URL and silently strips `q` plus any
   * non-default `limit` / `status`. `source` survived the bug only because
   * `fetchContentMasterWorkspaceConfig.fulfilled` re-seeds it from
   * `localStorage`; `q` / `limit` have no such backup.
   *
   * Dispatching inside an `if (!ref.current)` block during render is the
   * minimum mechanism that guarantees `useAppSelector` picks up the
   * URL-derived filters on the very first render, so the passive effect can
   * never fire with stale values. The `useLayoutEffect` below still handles
   * subsequent URL changes (e.g. browser back / forward).
   */
  const initialUrlSyncRef = useRef(false);
  if (!initialUrlSyncRef.current) {
    initialUrlSyncRef.current = true;
    const currentFilters = store.getState().contentMaster.filters;
    // Preserve the bootstrap-seeded `source` when the URL omits it, otherwise
    // we'd reset filters.source to '' on a fresh navigation to
    // `/features/content-master` and the list-fetch effect would bail out.
    const fromUrl = parseContentMasterListSearchParams(searchParams, currentFilters.source);
    if (!areContentMasterListFiltersEqual(fromUrl, currentFilters)) {
      store.dispatch(setContentMasterFilters(fromUrl));
    }
  }

  const {
    filters,
    list,
    listLoading,
    listError,
    workspaceConfig,
    wawiLanguages,
    wawiLanguagesLoading,
    wawiLanguagesError,
    bulkSelection,
    bulkGenerateSubmitting,
    bulkTranslateSubmitting,
    superBatchEstimate,
    superBatchEstimateLoading,
    superBatchSubmitting,
  } = useAppSelector((state) => state.contentMaster);

  /**
   * Seed `searchInput` from the URL - NOT from Redux's `filters.q`. On a fresh
   * mount Redux still has the slice's initial empty `q` (the URL → Redux sync
   * effect below hasn't dispatched yet), so reading `filters.q` here would
   * paint an empty input, fire the debounce effect with a stale `searchInput`
   * closure and race against the URL→Redux sync. That race was the actual
   * source of the "page mounts 50+ times with q=kinder" thrash.
   */
  const [searchInput, setSearchInput] = useState(
    () => parseContentMasterListSearchParams(searchParams).q
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // "Show all products" (includeUnassigned) - marketplace channels only.
  // Page-level state, re-seeded from localStorage whenever the channel changes.
  const sourceIsMarketplace = isMarketplaceSource(workspaceConfig?.platforms, filters.source);
  const [includeUnassigned, setIncludeUnassigned] = useState(() =>
    readIncludeUnassigned(filters.source)
  );
  useEffect(() => {
    setIncludeUnassigned(readIncludeUnassigned(filters.source));
  }, [filters.source]);

  /**
   * Tracks whether `searchInput` was changed by a real user keystroke since
   * mount. The debounce effect must not flush back to Redux when the only
   * reason `searchInput` differs from `filters.q` is that we just rehydrated
   * one of them from the URL - otherwise on first render we'd queue a 650ms
   * timer that overwrites `q=kinder` from the URL with `q=''`.
   */
  const userTypedSearchRef = useRef(false);
  /**
   * Last URL string we ourselves pushed via `setSearchParams`. Used by the
   * `filters → URL` effect to break the ping-pong with the `URL → filters`
   * effect: without this, on every URL canonicalization we'd dispatch into
   * Redux, the Redux change would re-canonicalize the URL, etc.
   */
  const lastPushedUrlRef = useRef<string | null>(null);
  /**
   * Workspace config is owned by the centralized bootstrap. The page only
   * reads it here; we render the empty / spinner state until it lands or
   * fails. No page-level fetch - that was a duplicate request source.
   */
  const wawiLangReady = workspaceConfig != null || wawiLanguagesError != null;

  /**
   * Highlight target for the row the user just edited on the product page.
   * The product page sets `pendingHighlightWawiArtikelId` in Redux right before
   * `navigate(-1)`; we capture it once via a lazy `useState` initializer (so
   * the value is locked in synchronously on mount, and StrictMode's double
   * initializer call is harmless because `store.getState()` is pure). The
   * Redux flag itself is cleared by an effect below so a subsequent unrelated
   * mount cannot re-trigger the highlight.
   * The matching row gets `--highlight` for ~10s, see main.css.
   */
  const [highlightedWawiArtikelId, setHighlightedWawiArtikelId] = useState<number | null>(() => {
    const pending = store.getState().contentMaster.pendingHighlightWawiArtikelId;
    return pending && pending > 0 ? pending : null;
  });

  /**
   * Companion one-shot flag: when the product page sets
   * `skipNextContentMasterListFetch`, we capture it into a ref the first time
   * the component renders and consume it inside the fetch effect below. A ref
   * (rather than state) is critical here so flipping the flag does NOT add a
   * dependency to the fetch effect - otherwise we'd ping-pong: skip → setState
   * → effect re-runs with `false` → full fetch fires anyway, defeating the
   * whole point.
   * In React 18 StrictMode dev double-mount this ref re-initializes on the
   * second mount but Redux has already been cleared by the consume-once effect
   * below, so the second pass falls through to a normal fetch - acceptable for
   * dev mode, harmless in production where double-mount does not happen.
   */
  const skipFirstListFetchRef = useRef<boolean | null>(null);
  if (skipFirstListFetchRef.current === null) {
    skipFirstListFetchRef.current =
      store.getState().contentMaster.skipNextContentMasterListFetch === true;
  }

  useEffect(() => {
    // Consume the pending hints from Redux exactly once on mount so they
    // cannot leak into a later navigation that revisits this page through a
    // different code path.
    dispatch(clearPendingContentMasterHighlight());
    dispatch(setSkipNextContentMasterListFetch(false));
    // Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const current = store.getState().contentMaster.filters;
    const fromUrl = parseContentMasterListSearchParams(searchParams, current.source);
    if (!areContentMasterListFiltersEqual(fromUrl, current)) {
      dispatch(setContentMasterFilters(fromUrl));
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    const next = serializeContentMasterListFilters(filters);
    const nextStr = next.toString();
    const currentStr = searchParams.toString();
    if (nextStr === currentStr) {
      // URL already in canonical form for these filters - remember it so a
      // subsequent identical filter change doesn't trigger a redundant push.
      lastPushedUrlRef.current = nextStr;
      return;
    }
    if (nextStr === lastPushedUrlRef.current) {
      // We already pushed this exact URL once; the URL → filters sync round-
      // trip just brought us back here. Skip to break the ping-pong.
      return;
    }
    lastPushedUrlRef.current = nextStr;
    setSearchParams(next, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    // Mirror Redux's `filters.q` into the local `searchInput` only when the
    // user is NOT in the middle of typing. `userTypedSearchRef` is flipped on
    // by the input's `onChange` and reset by the debounce flush, so this
    // effect handles only programmatic changes (URL sync, "reset filters",
    // back/forward navigation, etc.) and never clobbers in-flight typing.
    if (userTypedSearchRef.current) {
      return;
    }
    setSearchInput(filters.q);
  }, [filters.q]);

  useEffect(() => {
    if (!wawiLangReady) {
      return;
    }
    // Safety net: workspaceConfig is loaded but `filters.source` is empty
    // (URL had no source param AND Redux had no seeded value, e.g. very first
    // visit before the workspace-config seed branch ran). Resolve the default
    // channel ourselves rather than silently rendering a blank list.
    if (!filters.source && workspaceConfig) {
      const fallbackSource =
        pickDefaultContentMasterChannelId(workspaceConfig.platforms) ?? '';
      if (fallbackSource) {
        dispatch(setContentMasterFilters({ source: fallbackSource, page: 1 }));
      }
      return;
    }
    if (!filters.source) {
      return;
    }
    // Honour the one-shot skip set by the product edit page: keep the cached
    // list, scroll position and the locally-patched status from
    // `applyContentMasterListItemStatus` instead of refetching. Subsequent
    // effect runs (filter / page changes) fetch normally because the ref has
    // been flipped to false.
    if (skipFirstListFetchRef.current === true) {
      skipFirstListFetchRef.current = false;
      return;
    }
    dispatch(
      fetchContentMasterProducts({
        ...filters,
        // Marketplace sources only; ignored by the backend elsewhere anyway.
        includeUnassigned: sourceIsMarketplace && includeUnassigned,
        // The Wawi flags are kept in the URL across channels but only offered
        // (and applied) on marketplaces; `onlyNotActivated` is a 400 elsewhere.
        onlyInStock: sourceIsMarketplace && filters.onlyInStock,
        onlyWithEan: sourceIsMarketplace && filters.onlyWithEan,
        onlyNotActivated: sourceIsMarketplace && filters.onlyNotActivated,
      })
    );
  }, [dispatch, filters, wawiLangReady, sourceIsMarketplace, includeUnassigned]);

  useEffect(() => {
    if (!wawiLanguages.length || !filters.language.trim()) {
      return;
    }
    if (!isWawiLanguageCodeAllowed(filters.language, wawiLanguages)) {
      dispatch(setContentMasterFilters({ language: '', page: 1 }));
    }
  }, [dispatch, filters.language, wawiLanguages]);

  const prevListPageRef = useRef(filters.page);
  useEffect(() => {
    if (prevListPageRef.current !== filters.page) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      prevListPageRef.current = filters.page;
    }
  }, [filters.page]);

  // Start the clear-timer only once the highlighted product is actually in
  // the rendered list - otherwise a slow list fetch would waste the highlight
  // window before the user sees the row.
  useEffect(() => {
    if (highlightedWawiArtikelId == null) {
      return;
    }
    const present = list.items.some(
      (item) => item.wawiArtikelId === highlightedWawiArtikelId
    );
    if (!present) {
      return;
    }
    const timer = setTimeout(() => {
      setHighlightedWawiArtikelId(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [highlightedWawiArtikelId, list.items]);

  const flushSearchToStore = useCallback(
    (value: string) => {
      dispatch(
        setContentMasterFilters({
          q: value,
          page: 1,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    // The debounce ONLY exists to push real user keystrokes into Redux.
    // Without this guard, on initial mount with `?q=kinder` in the URL the
    // `searchInput`/`filters.q` pair is briefly out of sync (one is seeded
    // from the URL, the other from Redux's empty initial state), and the
    // 650ms timer would happily overwrite Redux with the stale value.
    if (!userTypedSearchRef.current) {
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      if (searchInput !== filters.q) {
        flushSearchToStore(searchInput);
      }
      userTypedSearchRef.current = false;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, filters.q, flushSearchToStore]);

  useEffect(() => {
    if (!listError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: listError,
      })
    );
  }, [dispatch, listError, t]);

  useEffect(() => {
    if (!wawiLanguagesError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: wawiLanguagesError,
      })
    );
  }, [dispatch, t, wawiLanguagesError]);

  /**
   * Surface bulk-submit / mark-for-edit errors as toasts. Quota errors are
   * intentionally not routed through `actionError` (the slice flips
   * `quotaExceededModalVisible` instead), so this effect only fires for the
   * generic failure path.
   */
  const actionError = useAppSelector((state) => state.contentMaster.actionError);
  useEffect(() => {
    if (!actionError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: actionError,
      })
    );
  }, [actionError, dispatch, t]);

  const listingSubmitting = useAppSelector(
    (state) => state.marketplaceListing.generateSubmitting
  );
  const listingError = useAppSelector((state) => state.marketplaceListing.generateError);
  useEffect(() => {
    if (!listingError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: listingError,
      })
    );
  }, [listingError, dispatch, t]);

  const totalPages = useMemo(() => {
    if (list.limit <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(list.total / list.limit));
  }, [list.limit, list.total]);

  const startItem = list.total === 0 ? 0 : (list.page - 1) * list.limit + 1;
  const endItem = Math.min(list.page * list.limit, list.total);

  // The tab also covers SCX listing drafts (`listing_generate_done`) - sum
  // both statuses for the badge.
  const inProgressCount =
    (list.processingStatusCounts[ContentMasterProductView.processingStatus.IN_PROGRESS] ?? 0) +
    (list.processingStatusCounts[
      ContentMasterProductView.processingStatus.LISTING_GENERATE_DONE
    ] ?? 0);
  const doneCount =
    list.processingStatusCounts[ContentMasterProductView.processingStatus.DONE] ?? 0;
  // The backend only returns `processingStatusCounts.new` while the `new` tab
  // itself is active (it equals the response `total`); on other tabs the count
  // is unavailable, so we render the tab without a count badge there.
  const newCount =
    list.processingStatusCounts[ContentMasterProductView.processingStatus.NEW];
  const allTabCount =
    filters.processingStatus === ''
      ? list.total
      : (list.totalWhenUnfiltered ?? list.total);

  const setProcessingTab = (status: ContentMasterProcessingStatusFilter) => {
    dispatch(setContentMasterFilters({ processingStatus: status, page: 1 }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && filters.page > 1) {
      dispatch(setContentMasterFilters({ page: filters.page - 1 }));
      return;
    }
    if (direction === 'next' && filters.page < totalPages) {
      dispatch(setContentMasterFilters({ page: filters.page + 1 }));
    }
  };

  /**
   * Build the absolute path to the product edit page using the same channel /
   * language combo the list is currently showing. Returned as a string so it
   * can feed both `<Link to=...>` (giving us middle-click and Ctrl+click "open
   * in new tab" for free) and any imperative navigation we still need.
   *
   * `filters.source` is the dynamic composite channel id (`plattform:N` or
   * `shop:N`) resolved from workspace-config; we forward it verbatim. If for
   * some reason it's still empty here (we
   * guard the click sites by hiding the row links until ready) we omit the
   * `source` param and let the product page resolve a default itself.
   */
  const buildProductPath = (
    wawiArtikelId: number,
    inProgressQueuePosition?: number
  ): string => {
    const query = new URLSearchParams();
    if (filters.source) {
      query.set('source', filters.source);
    }
    const lang = filters.language.trim();
    if (lang) {
      query.set('language', lang);
    } else {
      const fallback = pickDefaultWawiLanguageCode(wawiLanguages, workspaceConfig?.defaultLanguage);
      if (fallback) {
        query.set('language', fallback);
      }
    }
    if (
      typeof inProgressQueuePosition === 'number' &&
      inProgressQueuePosition >= 1 &&
      filters.processingStatus === CONTENT_MASTER_IN_PROGRESS_TAB_STATUS
    ) {
      query.set('inProgressPos', String(inProgressQueuePosition));
    }
    const qs = query.toString();
    return qs
      ? `/features/content-master/product/${wawiArtikelId}?${qs}`
      : `/features/content-master/product/${wawiArtikelId}`;
  };

  const productListLinkState = useMemo(
    (): ContentMasterListLocationState => ({
      fromContentMasterList: true,
      contentMasterListReturn: buildContentMasterListPath(filters),
    }),
    [filters]
  );

  const channelOptionsFlat = useMemo(
    () => buildContentMasterChannelOptions(workspaceConfig?.platforms),
    [workspaceConfig?.platforms]
  );
  const channelGroups = useMemo(
    () => buildContentMasterChannelGroups(workspaceConfig?.platforms),
    [workspaceConfig?.platforms]
  );
  /**
   * Maps a raw `source` literal (e.g. `shop:1`) to its workspace display name
   * for the "processed on other sources" tooltip. Falls back to the raw value
   * when the channel is not part of the current workspace snapshot.
   */
  const resolveSourceLabel = useCallback(
    (source: string): string => resolveContentMasterSourceLabel(channelOptionsFlat, source),
    [channelOptionsFlat]
  );
  const hasNoChannels = wawiLangReady && channelOptionsFlat.length === 0;
  /**
   * Single-channel workspaces don't need to render a select - but `source`
   * is still sent on every API call (via Redux) so behaviour is unchanged.
   */
  const showChannelSelect = channelOptionsFlat.length > 1;

  const listLanguageSelectValue = useMemo(() => {
    const raw = filters.language.trim();
    if (!raw) {
      return '';
    }
    if (!wawiLanguages.length) {
      return raw;
    }
    return isWawiLanguageCodeAllowed(raw, wawiLanguages) ? raw : '';
  }, [filters.language, wawiLanguages]);

  const showEmptyState = !listLoading && list.items.length === 0;

  // ---- Bulk selection / generation flow ---------------------------------

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);

  // ---- Search filter panel / super batch --------------------------------

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSuperBatchModalOpen, setIsSuperBatchModalOpen] = useState(false);
  /** Badge on the funnel icon: categories + committed field→phrase rows + marketplace Wawi flags. */
  const activeFilterCount =
    (filters.kCategory != null ? 1 : 0) +
    (filters.kWawiCategory != null ? 1 : 0) +
    filters.fieldFilters.length +
    (sourceIsMarketplace
      ? Number(filters.onlyInStock) +
        Number(filters.onlyWithEan) +
        Number(filters.onlyNotActivated)
      : 0);

  /**
   * Single Apply click of the filter modal: persist the marketplace
   * "show all products" toggle (localStorage-backed page state, not in the
   * URL) and commit every list filter in ONE dispatch, so the list is
   * refetched exactly once per Apply.
   */
  const handleFilterModalApply = useCallback(
    (result: ContentMasterFilterModalResult) => {
      if (sourceIsMarketplace && result.includeUnassigned !== includeUnassigned) {
        persistIncludeUnassigned(filters.source, result.includeUnassigned);
        setIncludeUnassigned(result.includeUnassigned);
      }
      dispatch(
        setContentMasterFilters({
          kCategory: result.kCategory,
          kCategoryName: result.kCategoryName,
          kWawiCategory: result.kWawiCategory,
          kWawiCategoryName: result.kWawiCategoryName,
          includeSubcategories: result.includeSubcategories,
          fieldFilters: result.fieldFilters,
          onlyInStock: result.onlyInStock,
          onlyWithEan: result.onlyWithEan,
          onlyNotActivated: result.onlyNotActivated,
          active: result.active,
          limit: result.limit,
          // The result set changes - restart from page 1.
          page: 1,
        })
      );
      setIsFilterModalOpen(false);
    },
    [dispatch, filters.source, includeUnassigned, sourceIsMarketplace]
  );

  /**
   * Body shared by `POST /bulk/estimate-by-filters` and
   * `POST /bulk/generate-by-filters`: the CURRENT search filter state (no
   * items list). `language` is required by the API, so the shop-default
   * fallback is resolved the same way as `buildProductPath`.
   */
  const superBatchParams = useMemo<ContentMasterSuperBatchParams | null>(() => {
    if (!filters.source) {
      return null;
    }
    const language =
      filters.language.trim() ||
      pickDefaultWawiLanguageCode(wawiLanguages, workspaceConfig?.defaultLanguage) ||
      '';
    if (!language) {
      return null;
    }
    return {
      source: filters.source,
      language,
      dataSourceId: workspaceConfig?.dataSourceId ?? null,
      kCategory: filters.kCategory,
      kWawiCategory: filters.kWawiCategory,
      includeSubcategories: filters.includeSubcategories,
      fieldFilters: filters.fieldFilters,
      processingStatus: filters.processingStatus || null,
      q: filters.q,
      // Same semantics as the list fetch: `true` when the checkbox is on,
      // omitted (all products) when off.
      active: filters.active ? true : null,
      // Same values the list search sends (marketplace channels only).
      onlyInStock: sourceIsMarketplace && filters.onlyInStock,
      onlyWithEan: sourceIsMarketplace && filters.onlyWithEan,
      onlyNotActivated: sourceIsMarketplace && filters.onlyNotActivated,
    };
  }, [filters, wawiLanguages, workspaceConfig, sourceIsMarketplace]);

  /**
   * Listing-flow state of the super-batch modal (SCX channels only; the modal
   * hides the toggle elsewhere). Reset to the classic flow on every open -
   * including after a channel change, so a non-marketplace `source` never
   * carries a stale `listingConfig` (backend 400).
   */
  const [superBatchListing, setSuperBatchListing] = useState<SuperBatchListingState>(
    defaultSuperBatchListingState
  );

  /**
   * "Also generate URL paths" opt-in of the classic-flow super batch
   * (`includeUrlPath`; default false - regenerating replaces live shop URLs).
   * Page-owned so the SAME value goes to `POST /bulk/estimate-by-filters` and
   * `POST /bulk/generate-by-filters` (otherwise the estimate diverges).
   */
  const [superBatchIncludeUrlPath, setSuperBatchIncludeUrlPath] = useState(false);

  /**
   * "What to do with empty fields?" of the classic-flow super batch
   * (`emptyFieldPolicy`; default `keepOriginal`). Sent to generate-by-filters
   * ONLY - estimate-by-filters ignores it (no cost impact), so changing it
   * does not invalidate the estimate.
   */
  const [superBatchEmptyFieldPolicy, setSuperBatchEmptyFieldPolicy] =
    useState<ContentMasterEmptyFieldPolicy>('keepOriginal');

  const closeSuperBatchModal = useCallback(() => {
    setIsSuperBatchModalOpen(false);
    dispatch(clearContentMasterSuperBatchEstimate());
  }, [dispatch]);

  const openSuperBatchModal = useCallback(() => {
    if (!superBatchParams) {
      return;
    }
    if (!bulkGenerationIncluded) {
      // Same gate as the id-based bulk generate: Starter plans get the
      // upgrade modal instead of the parameters modal.
      dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
      return;
    }
    // The modal opens with the parameter controls only - NO estimate request.
    // The estimate runs on demand via the modal's "Estimate" button.
    setSuperBatchListing(defaultSuperBatchListingState());
    setSuperBatchIncludeUrlPath(false);
    setSuperBatchEmptyFieldPolicy('keepOriginal');
    dispatch(clearContentMasterSuperBatchEstimate());
    setIsSuperBatchModalOpen(true);
  }, [superBatchParams, bulkGenerationIncluded, dispatch]);

  /**
   * Any change of the listing-flow parameters invalidates the current estimate
   * (the numbers depend on the selected attribute groups): the user must press
   * "Estimate" again, and the modal resets its consent checkbox.
   */
  const handleSuperBatchListingChange = useCallback(
    (next: SuperBatchListingState) => {
      setSuperBatchListing(next);
      dispatch(clearContentMasterSuperBatchEstimate());
    },
    [dispatch]
  );

  /** Same invalidation rule for the URL-path opt-in: the flag changes the
   * token estimate (one extra field per product), so re-estimating is mandatory. */
  const handleSuperBatchIncludeUrlPathChange = useCallback(
    (next: boolean) => {
      setSuperBatchIncludeUrlPath(next);
      dispatch(clearContentMasterSuperBatchEstimate());
    },
    [dispatch]
  );

  /**
   * "Estimate" button of the super-batch modal: runs the server estimate with
   * the CURRENT parameters - `listingConfig` on marketplace channels (the
   * platform decides the flow, there is no user toggle), classic otherwise.
   * The submit later sends exactly the same object.
   */
  const handleSuperBatchEstimate = useCallback(() => {
    if (!superBatchParams) {
      return;
    }
    dispatch(
      estimateContentMasterSuperBatch({
        ...superBatchParams,
        // Same flag as the submit below - estimate and generate must match.
        includeUrlPath: sourceIsMarketplace ? false : superBatchIncludeUrlPath,
        listingConfig: sourceIsMarketplace
          ? toSuperBatchListingConfig(superBatchListing)
          : null,
      })
    )
      .unwrap()
      .catch((rejected) => {
        // 403 pre-flight rejections open the shared quota / subscription
        // modals - close this one underneath. Other errors surface via the
        // actionError toast; the modal stays open so the user can retry.
        if (isSilentForbiddenRejection(rejected)) {
          setIsSuperBatchModalOpen(false);
        }
      });
  }, [
    superBatchParams,
    sourceIsMarketplace,
    superBatchListing,
    superBatchIncludeUrlPath,
    dispatch,
  ]);

  const handleConfirmSuperBatch = useCallback(async () => {
    if (!superBatchParams) {
      return;
    }
    try {
      const result = await dispatch(
        submitContentMasterSuperBatch({
          ...superBatchParams,
          // The exact flag the estimate in the modal was computed with.
          includeUrlPath: sourceIsMarketplace ? false : superBatchIncludeUrlPath,
          // Classic flow only; not sent to estimate-by-filters (it ignores it).
          emptyFieldPolicy: sourceIsMarketplace ? undefined : superBatchEmptyFieldPolicy,
          // The exact object the estimate in the modal was computed with.
          listingConfig: sourceIsMarketplace
            ? toSuperBatchListingConfig(superBatchListing)
            : null,
        })
      ).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          // Live data: the submit-time count may differ slightly from the
          // estimate - the toast uses the submit response value (spec).
          message: t('cm_super_batch_submitted', {
            products: (result.productsCount ?? 0).toLocaleString(),
          }),
        })
      );
      closeSuperBatchModal();
    } catch (rejected) {
      if (isSilentForbiddenRejection(rejected)) {
        closeSuperBatchModal();
        return;
      }
      // Other errors surface through the actionError toast; the modal stays
      // open so the user can retry.
    }
  }, [
    superBatchParams,
    sourceIsMarketplace,
    superBatchListing,
    superBatchIncludeUrlPath,
    superBatchEmptyFieldPolicy,
    dispatch,
    t,
    closeSuperBatchModal,
  ]);

  /** Product whose standalone assign-category modal is open (`null` = closed). */
  const [categoryModalFor, setCategoryModalFor] = useState<ContentMasterProductView | null>(
    null
  );

  /**
   * Wawi article ids visible on the current page that are NOT in an active
   * bulk task (`bulk_*_queued` / `bulk_*_in_progress`). Selectable rows.
   * Active items get a disabled checkbox so the user cannot try to re-queue
   * a product that is still being processed by a worker - backend would
   * silently `skipped[]` it anyway, but UX-wise we surface that earlier.
   */
  const selectableWawiArtikelIds = useMemo<number[]>(() => {
    return list.items
      .filter((it) => {
        const s = it.processingStatus;
        return (
          s !== ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED &&
          s !== ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS &&
          s !== ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED &&
          s !== ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS
        );
      })
      .map((it) => it.wawiArtikelId)
      .filter((v): v is number => typeof v === 'number');
  }, [list.items]);

  const selectedSet = useMemo(
    () => new Set(bulkSelection.wawiArtikelIds),
    [bulkSelection.wawiArtikelIds]
  );
  const selectedCount = bulkSelection.wawiArtikelIds.length;

  // Resolve each selected id to its product (for the bulk-generate category
  // gate). Ids not on the current page fall back to a minimal object - they are
  // treated as "category present" (not gated) and generated normally.
  const selectedProducts = useMemo<ContentMasterProductView[]>(
    () =>
      bulkSelection.wawiArtikelIds.map(
        (id) =>
          list.items.find((it) => it.wawiArtikelId === id) ??
          ({ wawiArtikelId: id } as ContentMasterProductView)
      ),
    [bulkSelection.wawiArtikelIds, list.items]
  );

  const allOnPageSelected =
    selectableWawiArtikelIds.length > 0 &&
    selectableWawiArtikelIds.every((id) => selectedSet.has(id));
  const someOnPageSelected =
    selectableWawiArtikelIds.some((id) => selectedSet.has(id));

  const handleToggleRowSelection = useCallback(
    (wawiArtikelId: number) => {
      dispatch(toggleContentMasterBulkSelection(wawiArtikelId));
    },
    [dispatch]
  );

  const handleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      dispatch(setContentMasterBulkSelection([]));
      return;
    }
    dispatch(setContentMasterBulkSelection(selectableWawiArtikelIds));
  }, [allOnPageSelected, dispatch, selectableWawiArtikelIds]);

  const handleClearSelection = useCallback(() => {
    dispatch(clearContentMasterBulkSelection());
  }, [dispatch]);

  const openBulkModal = useCallback(() => {
    if (selectedCount === 0 || !filters.source) {
      return;
    }
    if (!bulkGenerationIncluded) {
      dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
      return;
    }
    setIsBulkModalOpen(true);
  }, [bulkGenerationIncluded, dispatch, selectedCount, filters.source]);

  const closeBulkModal = useCallback(() => {
    setIsBulkModalOpen(false);
  }, []);

  /**
   * Bulk Translate entry point - same gates as Bulk Generate: a selection, a
   * channel, and a plan that includes bulk operations (the backend rejects
   * `/bulk/translate` with `content_master_subscription_insufficient` too).
   */
  const openTranslateModal = useCallback(() => {
    if (selectedCount === 0 || !filters.source) {
      return;
    }
    if (!bulkGenerationIncluded) {
      dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
      return;
    }
    setIsTranslateModalOpen(true);
  }, [bulkGenerationIncluded, dispatch, selectedCount, filters.source]);

  const closeTranslateModal = useCallback(() => {
    setIsTranslateModalOpen(false);
  }, []);

  /**
   * Submit the bulk TRANSLATE batch (`POST /bulk/translate`). The
   * accepted/skipped handling and the 403 quota / subscription contract are a
   * copy of the classic bulk generate flow; progress then runs through the
   * existing bulk pipeline (statuses / review / apply / retry unchanged).
   */
  const handleConfirmBulkTranslate = useCallback(
    async ({ sourceLanguage, targetLanguages, includeUrlPath }: BulkTranslateConfirmParams) => {
      if (selectedCount === 0) {
        return;
      }
      const items = bulkSelection.wawiArtikelIds.map((id) => ({ wawiArtikelId: id }));
      try {
        const result = await dispatch(
          submitContentMasterBulkTranslate({
            source: filters.source,
            sourceLanguage,
            targetLanguages,
            items,
            includeUrlPath,
            dataSourceId: workspaceConfig?.dataSourceId ?? null,
          })
        ).unwrap();
        const acceptedCount = result.accepted?.length ?? 0;
        const skippedCount = result.skipped?.length ?? 0;
        if (acceptedCount === 0 && skippedCount > 0) {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_bulk_submit_no_accepted'),
            })
          );
        } else {
          const parts = [
            t('content_master_bulk_translate_submit_success_accepted', { count: acceptedCount }),
          ];
          if (skippedCount > 0) {
            parts.push(t('content_master_bulk_submit_success_skipped', { count: skippedCount }));
          }
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: parts.join(' '),
            })
          );
        }
        setIsTranslateModalOpen(false);
      } catch (rejected) {
        // Quota / subscription rejections open the shared modals (dispatched
        // by the thunk) - closing this confirmation is enough.
        if (
          rejected === '__CONTENT_MASTER_QUOTA_EXCEEDED__' ||
          rejected === '__CONTENT_MASTER_SUBSCRIPTION_INSUFFICIENT__'
        ) {
          setIsTranslateModalOpen(false);
          return;
        }
        // Other errors surface through `actionError` → toast effect below.
      }
    },
    [
      bulkSelection.wawiArtikelIds,
      dispatch,
      filters.source,
      selectedCount,
      t,
      workspaceConfig?.dataSourceId,
    ]
  );

  const handleConfirmBulkGenerate = useCallback(
    async ({
      source,
      language,
      prepareListing,
      marketplace,
      generateRequired,
      generateRecommended,
      generateOther,
      generatePrefilled,
      onlyEmptyFields,
      listingItems,
      autoDetectCategory,
      includeUrlPath,
      emptyFieldPolicy,
    }: BulkGenerateConfirmParams) => {
      if (selectedCount === 0) {
        return;
      }
      const items = bulkSelection.wawiArtikelIds.map((id) => ({ wawiArtikelId: id }));

      // Marketplace-listing flow: queue the products via
      // `POST /marketplace-listing/generate`, then send the user to the listing
      // review page (status tabs). The classic Content Master bulk generate is
      // untouched below.
      if (prepareListing) {
        // Products without a category (and without auto-detect) were excluded in
        // the modal. If nothing remains, there is nothing to generate.
        const listingGenerateItems = listingItems ?? items;
        if (listingGenerateItems.length === 0) {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('marketplace_listing_submit_all_skipped'),
            })
          );
          return;
        }
        try {
          const result = await dispatch(
            submitMarketplaceListingGenerate({
              source,
              language,
              items: listingGenerateItems,
              marketplace,
              generateRequired,
              generateRecommended,
              generateOther,
              generatePrefilled,
              onlyEmptyFields,
              autoDetectCategory,
              dataSourceId: workspaceConfig?.dataSourceId ?? null,
            })
          ).unwrap();
          const acceptedCount = result.accepted?.length ?? 0;
          const skippedCount = result.skipped?.length ?? 0;
          if (acceptedCount === 0 && skippedCount > 0) {
            dispatch(
              addNotification({
                type: 'warning',
                title: t('notification_warning'),
                message: t('marketplace_listing_submit_no_accepted'),
              })
            );
          } else {
            const parts = [
              t('marketplace_listing_submit_accepted', { count: acceptedCount }),
            ];
            if (skippedCount > 0) {
              parts.push(t('marketplace_listing_submit_skipped', { count: skippedCount }));
            }
            dispatch(
              addNotification({
                type: 'success',
                title: t('notification_success'),
                message: parts.join(' '),
              })
            );
          }
          // Same behaviour as the classic bulk generate: just close the modal
          // (no redirect to the review page).
          setIsBulkModalOpen(false);
        } catch (rejected) {
          // 403 pre-flight rejections open the shared Content Master modals
          // (same contract as the classic bulk generate); everything else is
          // surfaced via the marketplaceListing slice error → toast above.
          const errorCode =
            rejected && typeof rejected === 'object' && 'errorCode' in rejected
              ? (rejected as { errorCode?: string | null }).errorCode
              : null;
          if (errorCode === 'content_master_quota_exceeded') {
            dispatch(setContentMasterQuotaModalVisible(true));
            setIsBulkModalOpen(false);
          } else if (errorCode === 'content_master_subscription_insufficient') {
            dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
            setIsBulkModalOpen(false);
          }
        }
        return;
      }

      try {
        const result = await dispatch(
          submitContentMasterBulkGenerate({
            source,
            language,
            items,
            includeUrlPath,
            emptyFieldPolicy,
            dataSourceId: workspaceConfig?.dataSourceId ?? null,
          })
        ).unwrap();
        const acceptedCount = result.accepted?.length ?? 0;
        const skippedCount = result.skipped?.length ?? 0;
        if (acceptedCount === 0 && skippedCount > 0) {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_bulk_submit_no_accepted'),
            })
          );
        } else {
          const parts = [
            t('content_master_bulk_submit_success_accepted', { count: acceptedCount }),
          ];
          if (skippedCount > 0) {
            parts.push(t('content_master_bulk_submit_success_skipped', { count: skippedCount }));
          }
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: parts.join(' '),
            })
          );
        }
        setIsBulkModalOpen(false);
      } catch (rejected) {
        // Quota error is handled by the global modal - slice's thunk dispatched
        // `setContentMasterQuotaModalVisible(true)` before rejecting, so
        // closing this confirmation is enough.
        if (rejected === '__CONTENT_MASTER_QUOTA_EXCEEDED__') {
          setIsBulkModalOpen(false);
          return;
        }
        if (rejected === '__CONTENT_MASTER_SUBSCRIPTION_INSUFFICIENT__') {
          setIsBulkModalOpen(false);
          return;
        }
        // Other errors surface through `actionError` → toast effect below.
      }
    },
    [
      bulkSelection.wawiArtikelIds,
      dispatch,
      selectedCount,
      t,
      workspaceConfig?.dataSourceId,
    ]
  );

  return (
    <div className="gpsr-content">
      <div className="gpsr-content-header-wrapper">
        <div className="gpsr-content-header-title">
          {t('content_master_page_title')}
        </div>
      </div>

      <div className="content-master-products-card">
        <div className="content-master-products-card__header">
          <div className="content-master-products-card__header-left">
            <div className="content-master-status-tabs" role="tablist" aria-label={t('content_master_status_tabs_aria')}>
              <button
                type="button"
                role="tab"
                aria-selected={filters.processingStatus === ''}
                className={
                  filters.processingStatus === ''
                    ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                    : 'content-master-status-tabs__tab'
                }
                onClick={() => setProcessingTab('')}
              >
                <span className="content-master-status-tabs__label">{t('content_master_status_tab_all')}</span>
                <span className="content-master-status-tabs__count">{allTabCount}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filters.processingStatus === ContentMasterProductView.processingStatus.NEW}
                className={
                  filters.processingStatus === ContentMasterProductView.processingStatus.NEW
                    ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                    : 'content-master-status-tabs__tab'
                }
                onClick={() => setProcessingTab(ContentMasterProductView.processingStatus.NEW)}
              >
                <span className="content-master-status-tabs__label">{t('content_master_status_tab_new')}</span>
                {newCount != null && (
                  <span className="content-master-status-tabs__count">{newCount}</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filters.processingStatus === CONTENT_MASTER_IN_PROGRESS_TAB_STATUS}
                className={
                  filters.processingStatus === CONTENT_MASTER_IN_PROGRESS_TAB_STATUS
                    ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                    : 'content-master-status-tabs__tab'
                }
                onClick={() => setProcessingTab(CONTENT_MASTER_IN_PROGRESS_TAB_STATUS)}
              >
                <span className="content-master-status-tabs__label">
                  {t('content_master_status_tab_in_progress')}
                </span>
                <span className="content-master-status-tabs__count">{inProgressCount}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={filters.processingStatus === ContentMasterProductView.processingStatus.DONE}
                className={
                  filters.processingStatus === ContentMasterProductView.processingStatus.DONE
                    ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                    : 'content-master-status-tabs__tab'
                }
                onClick={() => setProcessingTab(ContentMasterProductView.processingStatus.DONE)}
              >
                <span className="content-master-status-tabs__label">{t('content_master_status_tab_done')}</span>
                <span className="content-master-status-tabs__count">{doneCount}</span>
              </button>
            </div>
          </div>

          <div className="content-master-products-card__toolbar">
            <div className="content-master-search">
              <span className="content-master-search__icon" aria-hidden>
                <ContentMasterSearchIcon />
              </span>
              <input
                type="search"
                className="content-master-search__input"
                placeholder={t('content_master_search_placeholder')}
                value={searchInput}
                onChange={(e) => {
                  userTypedSearchRef.current = true;
                  setSearchInput(e.target.value);
                }}
                autoComplete="off"
                aria-label={t('content_master_search_aria')}
              />
            </div>
            <div className="content-master-toolbar-selects">
              {showChannelSelect && (
                <div
                  className="content-master-toolbar-select-wrap"
                  title={(() => {
                    const sel = channelOptionsFlat.find(
                      (o) => o.value === filters.source
                    );
                    if (!sel) {
                      return undefined;
                    }
                    if (sel.isMaster) {
                      return t('content_master_channel_master_tooltip');
                    }
                    return formatContentMasterChannelMeta(sel);
                  })()}
                >
                  <select
                    className="content-master-toolbar-select"
                    value={filters.source}
                    onChange={(event) => {
                      const next = event.target.value as ContentMasterListFilters['source'];
                      // Marktplatz channels require the marketplace-listing
                      // add-on: don't apply the filter (the controlled value
                      // keeps the previous channel) - offer the add-on instead.
                      if (
                        !marketplaceAddonActive &&
                        isMarketplaceSource(workspaceConfig?.platforms, next)
                      ) {
                        setIsAddonRequiredModalOpen(true);
                        return;
                      }
                      dispatch(
                        setContentMasterFilters({
                          source: next,
                          page: 1,
                          // Channel-specific filters cannot survive a source
                          // change: the category id belongs to the previous
                          // channel's tree and `mpAttr_*` keys are SCX-only
                          // (backend rejects them with 400 elsewhere).
                          kCategory: null,
                          kCategoryName: null,
                          kWawiCategory: null,
                          kWawiCategoryName: null,
                          includeSubcategories: false,
                          fieldFilters: filters.fieldFilters.filter(
                            (f) => !f.field.startsWith('mpAttr_')
                          ),
                          // Marketplace-only flag (400 on other sources).
                          onlyNotActivated:
                            isMarketplaceSource(workspaceConfig?.platforms, next) &&
                            filters.onlyNotActivated,
                        })
                      );
                      writeLastSelectedChannelId(
                        workspaceConfig?.dataSourceId ?? null,
                        next
                      );
                    }}
                    aria-label={t('content_master_all_channels')}
                  >
                    {channelGroups.map((group) => {
                      const groupLabel =
                        group.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM
                          ? t('content_master_channel_group_platforms')
                          : group.label ?? t('content_master_channel_group_shops');
                      return (
                        <optgroup key={group.key} label={groupLabel}>
                          {group.options.map((opt) => {
                            // Marktplatz channels without the add-on stay
                            // selectable (selection opens the add-on modal)
                            // but are rendered greyed-out with a hint.
                            const addonRequired =
                              !marketplaceAddonActive &&
                              isMarketplaceSource(
                                workspaceConfig?.platforms,
                                opt.value
                              );
                            return (
                              <option
                                key={opt.value}
                                value={opt.value}
                                className={
                                  addonRequired
                                    ? 'content-master-channel-option--addon-required'
                                    : undefined
                                }
                                title={
                                  addonRequired
                                    ? t('cm_addon_marketplace_menu_locked')
                                    : opt.isMaster
                                    ? t('content_master_channel_master_tooltip')
                                    : opt.hasContent
                                    ? formatContentMasterChannelMeta(opt)
                                    : t('content_master_channel_no_content_tooltip')
                                }
                              >
                                {opt.isMaster ? '★ ' : ''}
                                {opt.name}
                                {!opt.active
                                  ? ` ${t('content_master_channel_inactive_suffix')}`
                                  : ''}
                                {addonRequired ? ' · Add-on' : ''}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              )}
              <div className="content-master-toolbar-select-wrap hidden">
                <select
                  className="content-master-toolbar-select"
                  value={listLanguageSelectValue}
                  disabled={wawiLanguagesLoading && wawiLanguages.length === 0}
                  onChange={(event) =>
                    dispatch(
                      setContentMasterFilters({
                        language: event.target.value,
                        page: 1,
                      })
                    )
                  }
                  aria-label={t('content_master_language_label')}
                >
                  <option value="">{t('content_master_language_shop_default')}</option>
                  {wawiLanguages.map((lang, idx) => {
                    const code = wawiLanguageToApiValue(lang);
                    if (!code) {
                      return null;
                    }
                    return (
                      <option key={`${code}-${lang.kSprache ?? idx}`} value={code}>
                        {wawiLanguageLabel(lang, preferDeUi)}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <button
              type="button"
              className={
                activeFilterCount > 0
                  ? 'content-master-filter-toggle content-master-filter-toggle--active'
                  : 'content-master-filter-toggle'
              }
              onClick={() => setIsFilterModalOpen(true)}
              disabled={!filters.source}
              aria-haspopup="dialog"
              aria-label={t('content_master_filters_toggle_aria')}
              title={t('content_master_filters_toggle_aria')}
            >
              <ContentMasterFilterIcon />
              {activeFilterCount > 0 ? (
                <span className="content-master-filter-toggle__badge">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
          </div>
        
        </div>

        {hasNoChannels ? (
          <EmptyState
            title={t('content_master_no_channels_title')}
            description={t('content_master_no_channels_description')}
          />
        ) : showEmptyState ? (
          <EmptyState
            title={t('content_master_empty_title')}
            description={t('content_master_empty_description')}
          />
        ) : (
          <div className="content-master-table-panel">
            {listLoading && (
              <div className="content-master-table-panel__overlay" aria-busy="true" aria-live="polite">
                <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
              </div>
            )}
            <div className="content-master-table-wrapper">
              <table className="table gpsr-table content-master-table">
                <thead>
                  <tr>
                    <th
                      className={
                        selectableWawiArtikelIds.length === 0
                          ? 'content-master-table__checkbox-cell content-master-table__checkbox-cell--header'
                          : 'content-master-table__checkbox-cell content-master-table__checkbox-cell--header content-master-table__checkbox-cell--interactive'
                      }
                      onClick={() => {
                        if (selectableWawiArtikelIds.length === 0) {
                          return;
                        }
                        handleSelectAllOnPage();
                      }}
                    >
                      <input
                        type="checkbox"
                        className="content-master-checkbox"
                        aria-label={t('content_master_bulk_bar_select_all_on_page')}
                        checked={allOnPageSelected}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate =
                              !allOnPageSelected && someOnPageSelected;
                          }
                        }}
                        onChange={handleSelectAllOnPage}
                        disabled={selectableWawiArtikelIds.length === 0}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th>{t('content_master_summary_product')}</th>
                    <th>SKU</th>
                    <th>GTIN</th>
                    <th>{t('content_master_status')}</th>
                    <th className="content-master-table__edit-cell">{t('form_edit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((item, rowIndex) => {
                    const inProgressPos =
                      filters.processingStatus === CONTENT_MASTER_IN_PROGRESS_TAB_STATUS
                        ? (filters.page - 1) * filters.limit + rowIndex + 1
                        : undefined;
                    const productPath = item.wawiArtikelId
                      ? buildProductPath(item.wawiArtikelId, inProgressPos)
                      : null;
                    const rowClassName =
                      highlightedWawiArtikelId != null &&
                      item.wawiArtikelId === highlightedWawiArtikelId
                        ? 'content-master-table__row content-master-table__row--highlight'
                        : 'content-master-table__row';
                    const wawiId = item.wawiArtikelId;
                    const isRowSelectable =
                      typeof wawiId === 'number' && selectableWawiArtikelIds.includes(wawiId);
                    const isRowSelected = typeof wawiId === 'number' && selectedSet.has(wawiId);

                    return (
                      <tr
                        key={item.productKey || `${item.wawiArtikelId}-${item.sku}`}
                        className={rowClassName}
                      >
                        <td
                          className={
                            isRowSelectable
                              ? 'content-master-table__checkbox-cell content-master-table__checkbox-cell--interactive'
                              : 'content-master-table__checkbox-cell'
                          }
                          onClick={() => {
                            if (!isRowSelectable || typeof wawiId !== 'number') {
                              return;
                            }
                            handleToggleRowSelection(wawiId);
                          }}
                        >
                          <input
                            type="checkbox"
                            className="content-master-checkbox"
                            aria-label={t('content_master_bulk_bar_selected', { count: 1 })}
                            checked={isRowSelected}
                            disabled={!isRowSelectable}
                            onChange={() => {
                              if (typeof wawiId === 'number') {
                                handleToggleRowSelection(wawiId);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>
                          <div className="content-master-table__product-cell content-master-table__cell-body">
                            <ContentMasterProductThumb
                              imageUrl={item.mainImageUrl}
                              alt=""
                            />
                            {productPath ? (
                              <Link
                                to={productPath}
                                state={productListLinkState}
                                className="content-master-table__product-title-link"
                              >
                                {item.title || '-'}
                              </Link>
                            ) : (
                              <span className="content-master-table__title-text">
                                {item.title || '-'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="content-master-table__cell-body">
                            {item.sku || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="content-master-table__cell-body">
                            {item.gtin || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="content-master-table__cell-body content-master-status-cell">
                            <span className={getContentMasterStatusClassName(item.processingStatus)}>
                              {t(
                                contentMasterStatusLabelKey(
                                  item.processingStatus ??
                                    ContentMasterProductView.processingStatus.NEW,
                                  item.bulkOperation
                                )
                              )}
                            </span>
                            <ContentMasterOtherSourcesBadge
                              items={item.otherSourceProcessing}
                              resolveSourceLabel={resolveSourceLabel}
                            />
                            {item.marketplace?.status ===
                              MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED && (
                              <HoverTooltip
                                text={
                                  canCmWawiWrite
                                    ? t('marketplace_listing_no_category_tooltip')
                                    : t('subuser_no_permission_tooltip')
                                }
                              >
                                <button
                                  type="button"
                                  className="content-master-no-category-icon"
                                  onClick={() => setCategoryModalFor(item)}
                                  disabled={!canCmWawiWrite}
                                  aria-label={t('marketplace_listing_no_category_tooltip')}
                                >
                                  !
                                </button>
                              </HoverTooltip>
                            )}
                          </div>
                        </td>
                        <td className="content-master-table__edit-cell">
                          {productPath ? (
                            <div className="content-master-table__cell-body">
                              <Link
                                to={productPath}
                                state={productListLinkState}
                                className="content-master-table__edit-link"
                              >
                                {t('form_edit')}
                              </Link>
                            </div>
                          ) : (
                            <div className="content-master-table__cell-body">-</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {!hasNoChannels && (
        <div className="content-master-pagination-sticky">
          {/* Always visible (not only with a selection): it also hosts the
              filter-based "generate all" entry point. */}
          <div className="content-master-bulk-bar" role="toolbar" aria-live="polite">
            <span className="content-master-bulk-bar__count">
              {t('content_master_bulk_bar_selected', { count: selectedCount })}
            </span>
            <button
              type="button"
              className="content-master-bulk-bar__primary"
              onClick={openBulkModal}
              disabled={
                selectedCount === 0 ||
                !filters.source ||
                bulkGenerateSubmitting ||
                !canCmWrite
              }
              title={
                !canCmWrite
                  ? t('subuser_no_permission_tooltip')
                  : !bulkGenerationIncluded
                  ? t('cm_entitlements_starter_bulk_disabled')
                  : undefined
              }
            >
              {t('content_master_bulk_bar_open_bulk_generate')}
              {!bulkGenerationIncluded ? (
                <ContentMasterProBadge
                  className="content-master-bulk-bar__pro-badge"
                  title={t('cm_entitlements_starter_bulk_disabled')}
                />
              ) : null}
            </button>
            <button
              type="button"
              className="content-master-bulk-bar__primary"
              onClick={openTranslateModal}
              disabled={
                selectedCount === 0 ||
                !filters.source ||
                bulkTranslateSubmitting ||
                !canCmWrite
              }
              title={
                !canCmWrite
                  ? t('subuser_no_permission_tooltip')
                  : !bulkGenerationIncluded
                  ? t('cm_entitlements_starter_bulk_disabled')
                  : undefined
              }
            >
              {t('content_master_bulk_bar_open_bulk_translate')}
              {!bulkGenerationIncluded ? (
                <ContentMasterProBadge
                  className="content-master-bulk-bar__pro-badge"
                  title={t('cm_entitlements_starter_bulk_disabled')}
                />
              ) : null}
            </button>
            <button
              type="button"
              className="content-master-bulk-bar__primary"
              onClick={openSuperBatchModal}
              disabled={
                !canCmWrite ||
                superBatchEstimateLoading ||
                superBatchSubmitting ||
                !superBatchParams
              }
              title={
                !canCmWrite
                  ? t('subuser_no_permission_tooltip')
                  : !bulkGenerationIncluded
                  ? t('cm_entitlements_starter_bulk_disabled')
                  : undefined
              }
            >
              {t('cm_super_batch_button')}
              {!bulkGenerationIncluded ? (
                <ContentMasterProBadge
                  className="content-master-bulk-bar__pro-badge"
                  title={t('cm_entitlements_starter_bulk_disabled')}
                />
              ) : null}
            </button>
            <button
              type="button"
              className="content-master-bulk-bar__link"
              onClick={handleSelectAllOnPage}
              disabled={selectableWawiArtikelIds.length === 0}
            >
              {t('content_master_bulk_bar_select_all_on_page')}
            </button>
            <button
              type="button"
              className="content-master-bulk-bar__link content-master-bulk-bar__link--cancel"
              onClick={handleClearSelection}
              disabled={selectedCount === 0}
            >
              {t('content_master_bulk_bar_cancel')}
            </button>
          </div>
          {list.items.length > 0 && (
            <Pagination
              startItem={startItem}
              endItem={endItem}
              total={list.total}
              totalPages={totalPages}
              page={list.page}
              loading={listLoading}
              handlePageChange={handlePageChange}
              onGotoPage={(targetPage) =>
                dispatch(setContentMasterFilters({ page: targetPage }))
              }
            />
          )}
        </div>
      )}

      <BulkGenerateConfirmModal
        isOpen={isBulkModalOpen}
        onClose={closeBulkModal}
        selectedCount={selectedCount}
        platforms={workspaceConfig?.platforms}
        languages={wawiLanguages}
        defaultWorkspaceLanguage={workspaceConfig?.defaultLanguage ?? null}
        initialSource={filters.source}
        initialLanguage={filters.language}
        preferDeUi={preferDeUi}
        isSubmitting={bulkGenerateSubmitting || listingSubmitting}
        dataSourceId={workspaceConfig?.dataSourceId ?? null}
        selectedProducts={selectedProducts}
        publicConfigs={workspaceConfig?.publicConfigs ?? null}
        onConfirm={handleConfirmBulkGenerate}
      />

      <BulkTranslateConfirmModal
        isOpen={isTranslateModalOpen}
        onClose={closeTranslateModal}
        selectedCount={selectedCount}
        languages={wawiLanguages}
        defaultWorkspaceLanguage={workspaceConfig?.defaultLanguage ?? null}
        initialLanguage={filters.language}
        preferDeUi={preferDeUi}
        isSubmitting={bulkTranslateSubmitting}
        publicConfigs={workspaceConfig?.publicConfigs ?? null}
        onConfirm={handleConfirmBulkTranslate}
      />

      <ContentMasterFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        source={filters.source}
        dataSourceId={workspaceConfig?.dataSourceId ?? null}
        language={filters.language}
        sourceIsMarketplace={sourceIsMarketplace}
        kCategory={filters.kCategory}
        kCategoryName={filters.kCategoryName}
        kWawiCategory={filters.kWawiCategory}
        kWawiCategoryName={filters.kWawiCategoryName}
        includeSubcategories={filters.includeSubcategories}
        fieldFilters={filters.fieldFilters}
        onlyInStock={filters.onlyInStock}
        onlyWithEan={filters.onlyWithEan}
        onlyNotActivated={filters.onlyNotActivated}
        active={filters.active}
        limit={filters.limit}
        includeUnassigned={sourceIsMarketplace && includeUnassigned}
        onApply={handleFilterModalApply}
      />

      <SuperBatchConfirmModal
        isOpen={isSuperBatchModalOpen}
        onClose={closeSuperBatchModal}
        estimate={superBatchEstimate}
        estimateLoading={superBatchEstimateLoading}
        isSubmitting={superBatchSubmitting}
        listingAvailable={sourceIsMarketplace}
        listing={superBatchListing}
        onListingChange={handleSuperBatchListingChange}
        includeUrlPath={superBatchIncludeUrlPath}
        onIncludeUrlPathChange={handleSuperBatchIncludeUrlPathChange}
        emptyFieldPolicy={superBatchEmptyFieldPolicy}
        onEmptyFieldPolicyChange={setSuperBatchEmptyFieldPolicy}
        onEstimate={handleSuperBatchEstimate}
        onConfirm={handleConfirmSuperBatch}
      />

      <MarketplaceCategoryModal
        isOpen={categoryModalFor != null}
        onClose={() => setCategoryModalFor(null)}
        source={filters.source}
        wawiArtikelId={categoryModalFor?.wawiArtikelId ?? null}
        productTitle={categoryModalFor?.title}
        dataSourceId={workspaceConfig?.dataSourceId ?? null}
      />

      <ContentMasterQuotaExceededModal />

      <ContentMasterAddonRequiredModal
        isOpen={isAddonRequiredModalOpen}
        onClose={() => setIsAddonRequiredModalOpen(false)}
      />
    </div>
  );
};

export default ContentMasterPage;
